/**
 * Rail model shared by the desktop rail (HubRail) and the phone drawer (HubDrawer):
 * the rows for the current user and location, their style, and arrow-key focus.
 */
import type React from 'react';
import { useLocation } from 'react-router-dom';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTableCellsLarge, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { DWA } from '../theme/dwaTokens';
import { resolveHubLocation } from './hubs';
import type { Hub } from './hubs';
import { hubHasFlyout, railGroups } from './hubNav';

export type RailEntry =
  | { key: string; kind: 'link'; label: string; icon: IconDefinition; to: string; active: boolean }
  | { key: string; kind: 'menu'; label: string; icon: IconDefinition; hub?: Hub; active: boolean }
  | { key: string; kind: 'divider' };

/** Rail rows for the current user and location (shared with the mobile drawer). */
export function useRailEntries(): RailEntry[] {
  const { user } = useAuth();
  const { pathname, search } = useLocation();
  const current = resolveHubLocation(pathname, search).hub?.id;
  const groups = railGroups(user);
  const hubEntry = (hub: Hub): RailEntry =>
    hubHasFlyout(hub.id, user)
      ? { key: hub.id, kind: 'menu', label: hub.title, icon: hub.icon, hub, active: current === hub.id }
      : { key: hub.id, kind: 'link', label: hub.title, icon: hub.icon, to: hub.route, active: current === hub.id };

  const entries: RailEntry[] = [
    { key: 'all-hubs', kind: 'link', label: 'All Hubs', icon: faTableCellsLarge, to: '/', active: false },
    ...groups.top.map(hubEntry),
    { key: 'pinned', kind: 'menu', label: 'Pinned', icon: faThumbtack, active: false },
  ];
  if (groups.main.length > 0) entries.push({ key: 'divider-main', kind: 'divider' }, ...groups.main.map(hubEntry));
  if (groups.bottom.length > 0) entries.push({ key: 'divider-admin', kind: 'divider' }, ...groups.bottom.map(hubEntry));
  return entries;
}

export function rowStyle(active: boolean, collapsed: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    width: '100%',
    minHeight: 40,
    padding: collapsed ? '0 0' : '0 0.75rem',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 999,
    border: 'none',
    textDecoration: 'none',
    fontFamily: DWA.font,
    fontSize: '0.9rem',
    fontWeight: active ? 700 : 500,
    color: active ? DWA.gold : DWA.onDark,
    cursor: 'pointer',
    textAlign: 'left',
    ...(active ? { background: DWA.railActive } : { background: 'transparent' }),
  };
}

/** Move focus among `[data-menu-item]` / `[data-rail-row]` elements with the arrow keys. */
export function moveFocus(container: HTMLElement | null, selector: string, key: string): boolean {
  if (!container) return false;
  const els = Array.from(container.querySelectorAll<HTMLElement>(selector));
  if (els.length === 0) return false;
  const i = els.indexOf(document.activeElement as HTMLElement);
  let next = i;
  if (key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % els.length;
  else if (key === 'ArrowUp') next = i < 0 ? els.length - 1 : (i - 1 + els.length) % els.length;
  else if (key === 'Home') next = 0;
  else if (key === 'End') next = els.length - 1;
  else return false;
  els[next]?.focus();
  return true;
}
